import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch, openAuthenticatedFile, isServerProxyDocUrl } from "../utils/auth";
import SideBar from "./SideBar";
import UserProfileMenu from "./UserProfileMenu";
import ConfirmDialog from "./ConfirmDialog";
import FeedbackModal from "./FeedbackModal";

const FONT = "'Space Grotesk', sans-serif";

// ── Session persistence ──────────────────────────────────────────────────────
// Persist the active batch case id across page refreshes so F5 reopens the same
// case rather than landing on an empty "Choose a case" screen.  sessionStorage
// is per-tab (unlike localStorage), which avoids leaking across tabs.
const BATCH_ACTIVE_KEY = "batch_active_case_id";
const persistActiveCase = (caseId) => {
  try { if (caseId) sessionStorage.setItem(BATCH_ACTIVE_KEY, caseId); else sessionStorage.removeItem(BATCH_ACTIVE_KEY); } catch { /* private mode */ }
};
const readPersistedCase = () => {
  try { return sessionStorage.getItem(BATCH_ACTIVE_KEY) || null; } catch { return null; }
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// Bug / case ticket links.  The ingested `url` payload of the bug and case
// collections points at THIS app's own ticket pages, with the prod host baked
// in (jira_bug_tickets_new: https://zscaler.artasupport.com/jira/<issueKey>,
// sfdc_cases_csv_new: https://zscaler.artasupport.com/cases/<caseId>).  The
// legacy sfdc_cases_csv carries a placeholder instead
// (https://example.com/<caseId>), which lands on a foreign site.  Re-point both
// at the ticket route on the origin the user is actually signed in to, so the
// link opens the ticket on staging/local too and never leaves the app.
const APP_TICKET_HOSTS = new Set(["zscaler.artasupport.com"]);
const PLACEHOLDER_HOSTS = new Set(["example.com", "www.example.com"]);
const APP_TICKET_PATH_RE = /^\/(?:jira|cases?)\/[^/?#]+\/?$/;
// Salesforce Case record ids: key prefix 500, 15- or 18-char.
const SFDC_CASE_ID_RE = /^\/(500[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?)\/?$/;
const normalizeTicketUrl = (raw) => {
  let u;
  try { u = new URL(raw); } catch { return raw; }
  const host = u.hostname.toLowerCase();
  if ((APP_TICKET_HOSTS.has(host) || host === window.location.hostname) && APP_TICKET_PATH_RE.test(u.pathname)) {
    return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
  }
  const m = PLACEHOLDER_HOSTS.has(host) && u.pathname.match(SFDC_CASE_ID_RE);
  if (m) return `${window.location.origin}/cases/${m[1]}`;
  return raw;
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const subjectFromFirstMessage = (text, maxLen = 60) => {
  const first = (text || "").split("\n").map((l) => l.trim()).find(Boolean) || "";
  if (!first) return null;
  const capped = first.length <= maxLen ? first : first.slice(0, maxLen).replace(/\s+\S*$/, "…");
  return capped.charAt(0).toUpperCase() + capped.slice(1);
};

// Token used by Claude to indicate where user input is required in batch mode.
const ANSWER_TOKEN = "[[answer]]";
const ANSWER_TOKEN_RE = /\[\[answer\]\]/gi;

// The agent's ::sources … ::end manifest — copied VERBATIM from
// src/components/dashboard5/utils/formatMessage.js (MANIFEST_RE) rather than
// imported: that module is the copilot UI, which must not change here, and a
// later edit to the batch strip must not be able to break copilot's (or vice
// versa). Batch has no card renderer for it, so it is stripped outright
// rather than parsed.
const MANIFEST_RE = /^[ \t]*::sources[ \t]*\r?\n[\s\S]*?^[ \t]*::end[ \t]*\r?\n?/gim;

// ── Export to Markdown ───────────────────────────────────────────────────────

function buildExportMarkdown(activeCase, messages) {
  const created = activeCase.created_at
    ? new Date(activeCase.created_at).toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" })
    : "—";
  const agentMsgs = messages.filter((m) => m.role === "assistant" && m.latency_ms);
  const totalMs = agentMsgs.reduce((s, m) => s + (m.latency_ms || 0), 0);
  const totalCost = agentMsgs.reduce((s, m) => s + (m.cost_usd || 0), 0);

  const lines = [
    `# ${activeCase.subject || "Untitled Case"}`,
    "",
    `**Case ID:** ${activeCase.case_id}  `,
    `**From:** ${activeCase.from_name} → ${activeCase.to_name}  `,
    `**Status:** ${activeCase.status || "open"}${activeCase.phase ? ` | **Phase:** ${activeCase.phase}` : ""}  `,
    `**Created:** ${created}  `,
    `**Turns:** ${agentMsgs.length}${totalMs ? ` | **Total latency:** ${(totalMs / 1000).toFixed(1)}s` : ""}${totalCost > 0 ? ` | **Total cost:** $${totalCost.toFixed(4)}` : ""}`,
    "",
    "---",
  ];

  for (const msg of messages) {
    const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    const isUser = msg.role === "user";
    lines.push("");
    lines.push(`## ${isUser ? "📨 " : "🤖 "}${msg.from}${ts ? ` — ${ts}` : ""}`);
    if (!isUser && (msg.latency_ms || msg.cost_usd)) {
      const parts = [];
      if (msg.latency_ms) parts.push(`Latency: ${(msg.latency_ms / 1000).toFixed(1)}s`);
      if (msg.cost_usd) parts.push(`Cost: $${msg.cost_usd.toFixed(4)}`);
      lines.push(`*${parts.join(" · ")}*`);
    }
    lines.push("");
    let content = (msg.content || "").replace(MANIFEST_RE, "").replace(ANSWER_TOKEN_RE, "**Your answer:** ___________");
    lines.push(content);
    lines.push("");
    lines.push("---");
  }

  lines.push("");
  lines.push("*Exported from Arta Batch Diagnostics*");
  return lines.join("\n");
}

function downloadMarkdown(activeCase, messages) {
  const md = buildExportMarkdown(activeCase, messages);
  const date = new Date().toISOString().slice(0, 10);
  const filename = `batch-${activeCase.case_id}-${date}.md`;
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const formatBotMessage = (text) => {
  if (!text) return "";
  let t = String(text).replace(MANIFEST_RE, "");
  t = t.split("\n").filter((l) => !/^\*\*Note -/.test(l.trim())).join("\n");
  t = t.replace(/\p{Extended_Pictographic}/gu, "").replace(/  +/g, " ");
  // HTML-escape dangerous characters BEFORE markdown-to-HTML regexes run.
  // This prevents raw <style>, <!--, <textarea> etc. in model output from
  // being interpreted as HTML when rendered via dangerouslySetInnerHTML.
  // Subsequent regexes produce their own safe HTML tags from markdown syntax.
  t = t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  t = t.replace(/!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
  // Strip image markdown with non-http URLs (show alt text only).
  t = t.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  t = t.replace(/^#{1,6}\s+/gm, "");
  t = t.replace(/^&gt;\s?/gm, "");
  t = t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.*?)\*/g, "$1");
  t = t.replace(/^\s*[-]\s+/gm, "  ");
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => {
    // S3-proxied citation links (/api/images/...) are auth-gated — open via
    // authenticated fetch instead of a plain navigation that would 401.
    if (isServerProxyDocUrl(url)) return `<a tabindex="0" class="citation-link text-blue-600 hover:text-blue-800 underline" data-doc-url="${url}" style="cursor:pointer;">${label}</a>`;
    return `<a href="${normalizeTicketUrl(url)}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${label}</a>`;
  });
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
  // Strip remaining markdown links whose URL is NOT absolute http(s) — e.g.
  // relative paths, empty hrefs, or IDs the LLM wrapped in link syntax.  Show
  // only the label so they never resolve against the app origin and navigate to
  // an internal route like /ent-user.
  t = t.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  const urlRegex = /(?<!")\b(https?:\/\/(?:[^\s<>)"&`]|&(?!lt;|gt;))+)(?!")/gi;
  t = t.replace(urlRegex, (match) => `<a href="${normalizeTicketUrl(match)}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`);
  t = t.replace(/(\|.+\|(?:\n\|[:\s\-|]+\|)?(?:\n\|.+\|)+)/g, (match) => {
    const lines = match.trim().split("\n").filter((l) => l.trim());
    if (lines.length < 2) return match;
    let html = '<table class="border-collapse border border-gray-300 my-3" style="width:100%;font-size:13px"><thead><tr>';
    lines[0].split("|").filter((c) => c.trim()).forEach((c) => {
      html += `<th class="border border-gray-300 px-3 py-2 bg-gray-100 font-semibold text-left">${c.trim()}</th>`;
    });
    html += "</tr></thead><tbody>";
    for (let i = 2; i < lines.length; i++) {
      html += "<tr>";
      lines[i].split("|").filter((c) => c.trim()).forEach((c) => {
        html += `<td class="border border-gray-300 px-3 py-2">${c.trim()}</td>`;
      });
      html += "</tr>";
    }
    return html + "</tbody></table>";
  });
  t = t.replace(/^---$/gm, '<hr class="my-3 border-gray-300">');
  // [[answer]] → read-only answer placeholder in the thread view
  t = t.replace(ANSWER_TOKEN_RE,
    '<div class="my-2 px-3 py-1.5 border border-dashed border-cyan-300 rounded text-xs text-cyan-600 bg-cyan-50/60">Your answer: ___________</div>');
  t = t.replace(/\n/g, "<br>");
  return t;
};

// ── Section grouper ───────────────────────────────────────────────────────────
// Groups parsed segments into { title, items[] } sections.
// ALL-CAPS text segments become section headers; everything else is body content.

const ALL_CAPS_RE = /^[A-Z][A-Z\s\/&]{3,}$/;
const SECTION_SEP_RE = /^---+$/; // "---" written by Claude closes the current section

function segmentsToSections(segments) {
  const sections = [];
  let current = { title: null, items: [] };

  const flushTextLines = (lines) => {
    const t = lines.join("\n").trim();
    if (t) current.items.push({ type: "text", content: t });
  };

  for (const seg of segments) {
    if (seg.type !== "text") {
      current.items.push(seg);
      continue;
    }
    const lines = seg.content.split("\n");
    let textBuf = [];
    for (const line of lines) {
      const t = line.trim();
      if (t && ALL_CAPS_RE.test(t)) {
        // New ALL-CAPS section header
        flushTextLines(textBuf);
        textBuf = [];
        if (current.items.length || current.title) sections.push(current);
        current = { title: t, items: [] };
      } else if (SECTION_SEP_RE.test(t)) {
        // "---" — close current section, start plain (null-title) block for closing text
        flushTextLines(textBuf);
        textBuf = [];
        if (current.items.length || current.title) sections.push(current);
        current = { title: null, items: [] };
        // Separator line itself is not included in output
      } else {
        textBuf.push(line);
      }
    }
    flushTextLines(textBuf);
  }
  if (current.items.length || current.title) sections.push(current);
  return sections;
}

// ── Full email HTML document builder ──────────────────────────────────────────
// Generates <!DOCTYPE html> with <style> block — dynamic, no hardcoded content.
// Rendered by an iframe before copying so styles apply properly.

// Strip email header lines and salutations the agent prepends.
function stripEmailPreamble(text) {
  const headerRe = /^(Re|Fwd?|Subject|From|To|Cc|Bcc|Date)\s*:/i;
  const lines = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  let i = 0;
  // Skip leading blank lines first, then header lines, then blank lines after headers
  while (i < lines.length && lines[i].trim() === "") i++; // skip leading blanks
  while (i < lines.length && headerRe.test(lines[i].trim())) i++; // skip header lines
  while (i < lines.length && lines[i].trim() === "") i++; // skip blanks after headers
  // If we only consumed blanks (no actual headers found), also try a regex fallback
  let result = lines.slice(i).join("\n").trimStart();
  // Safety net: strip any Subject: line that slipped through
  result = result.replace(/^Subject:[^\n]*\n*/i, "").trimStart();
  // Remove "Dear ...,\n" salutation
  result = result.replace(/^Dear [^\n]{1,80}[,.]?\s*\n*/i, "").trimStart();
  return result;
}

// All styles are inline — no <style> block.
// Gmail/Outlook strip <style> on paste but preserve inline styles.
// Copy is synchronous (no setTimeout/await) so Chrome's user-gesture context is preserved.

// ── Shared inline style tokens ────────────────────────────────────────────────
// Single source of truth — used by both the email copy template and the compose editor.

const IS = {
  wrap:        "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:14px;line-height:1.7;color:#333;max-width:680px;",
  section:     "border:1px solid #cce8f0;border-radius:6px;margin-bottom:24px;overflow:hidden;",
  secTitle:    "font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;color:#ffffff;background-color:#06b6d4;padding:8px 14px;display:block;margin:0;",
  secBody:     "padding:14px 14px 4px 14px;",
  intro:       "margin-bottom:20px;",
  secPara:     "color:#444;font-size:14px;margin-bottom:10px;",
  qBlock:      "margin-bottom:16px;padding-bottom:16px;border-bottom:1px solid #f0f0f0;",
  qBlockLast:  "margin-bottom:0;padding-bottom:0;border-bottom:none;",
  qNum:        "font-weight:600;color:#253439;font-size:14px;margin-bottom:8px;",
  optUl:       "list-style:none;margin:0 0 10px 0;padding:0;",
  optLi:       "margin-bottom:5px;font-size:13px;color:#555;",
  box:         "display:inline-block;width:20px;height:18px;border:1px solid #ccc;border-radius:2px;text-align:center;line-height:17px;font-weight:700;font-size:11px;color:#666;margin-right:8px;",
  // copy template: blank underline   compose template: teal editable box
  answer:      "margin-top:10px;padding-top:10px;border-top:1px dotted #ddd;font-size:13px;color:#666;",
  answerBox:   "margin-top:10px;margin-bottom:18px;padding:8px 12px;border:1.5px solid #06b6d4;border-radius:4px;background:#f0fdfa;min-height:30px;font-size:13px;color:#0e7490;",
  quoted:      "margin-top:20px;border-left:2px solid #ddd;padding-left:12px;color:#999;font-size:13px;",
  quotedMeta:  "font-size:12px;margin-bottom:4px;",
  quotedText:  "font-style:italic;",
};

// ── Shared email body renderer ────────────────────────────────────────────────

// Replies can carry markdown emphasis (e.g. "**Root Cause:**"). Email clients show
// the literal asterisks, so convert inline emphasis to HTML here.
function emailInlineMarkdown(s) {
  return s
    .replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*\w])\*([^*\n]+?)\*(?![*\w])/g, "$1<em>$2</em>")
    .replace(/`([^`\n]+?)`/g, '<code style="font-family:Consolas,monospace;background:#f3f4f6;padding:1px 4px;border-radius:3px;">$1</code>');
}

function renderTextWithAnswers(text) {
  const parts = text.split(ANSWER_TOKEN_RE);
  return parts.map((part, i) => {
    const trimmed = part.replace(/^\n+/, "").replace(/\n+$/, "");
    const p = trimmed ? `<p style="${IS.secPara}">${emailInlineMarkdown(trimmed).replace(/\n/g, "<br>")}</p>` : "";
    const ans = i < parts.length - 1
      ? `<div style="${IS.answerBox}"><strong style="color:#0e7490;">Your answer:</strong>&nbsp;</div>`
      : "";
    return p + ans;
  }).join("");
}

function renderEmailBody(rawContent) {
  if (!rawContent?.trim()) return "";
  const sections = segmentsToSections([{ type: "text", content: rawContent }]);
  return sections.map((sec) => {
    const itemsHTML = sec.items.map((item) => renderTextWithAnswers(item.content)).join("");
    if (!sec.title) return `<div style="${IS.intro}">${itemsHTML}</div>`;
    return `<div style="${IS.section}"><div style="${IS.secTitle}">${sec.title}</div><div style="${IS.secBody}">${itemsHTML}</div></div>`;
  }).join("");
}

function buildEmailHTML(message) {
  const isUser = message.role === "user";
  const rawContent = isUser ? message.content : stripEmailPreamble(message.content);
  const bodyContent = isUser
    ? `<p style="${IS.secPara};white-space:pre-wrap;">${rawContent}</p>`
    : renderEmailBody(rawContent);
  return `<div style="${IS.wrap}">${bodyContent}</div>`;
}

// Synchronous copy — must stay within user-gesture context (no setTimeout, no await).
// contentEditable div at top:-9999px is rendered by browser (unlike display:none)
// so execCommand captures rich formatted HTML with inline styles intact.
function copyMessageToClipboard(message) {
  const html = buildEmailHTML(message);
  const el = document.createElement("div");
  el.contentEditable = "true";
  el.innerHTML = html;
  el.style.cssText = "position:absolute;top:-9999px;left:0;width:700px;";
  document.body.appendChild(el);
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  document.execCommand("copy");
  sel.removeAllRanges();
  document.body.removeChild(el);
  return Promise.resolve(true);
}

// formatForEmail is used inside buildEmailHTML for plain/diagnostic messages.
function formatForEmail(text) {
  if (!text) return "";
  let t = String(text).split("\n").filter((l) => !/^\*\*Note -/.test(l.trim())).join("\n");
  t = t.replace(/^#{3}\s+(.+)$/gm, "<h3>$1</h3>");
  t = t.replace(/^#{2}\s+(.+)$/gm, "<h2>$1</h2>");
  t = t.replace(/^#{1}\s+(.+)$/gm, "<h1>$1</h1>");
  t = t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.*?)\*/g, "<em>$1</em>");
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => `<a href="${normalizeTicketUrl(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`);
  t = t.replace(/!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Strip non-http markdown links so they cannot resolve as relative hrefs.
  t = t.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  t = t.replace(/^---$/gm, "<hr>");
  t = t.replace(/(\|.+\|(?:\n\|[:\s\-|]+\|)?(?:\n\|.+\|)+)/g, (match) => {
    const lines = match.trim().split("\n").filter((l) => l.trim());
    if (lines.length < 2) return match;
    let html = "<table><thead><tr>";
    lines[0].split("|").filter((c) => c.trim()).forEach((c) => { html += `<th>${c.trim()}</th>`; });
    html += "</tr></thead><tbody>";
    for (let i = 2; i < lines.length; i++) {
      html += "<tr>";
      lines[i].split("|").filter((c) => c.trim()).forEach((c) => { html += `<td>${c.trim()}</td>`; });
      html += "</tr>";
    }
    return html + "</tbody></table>";
  });
  const processedLines = [];
  let inList = false;
  for (const line of t.split("\n")) {
    if (/^\s*[-*]\s+(.+)/.test(line)) {
      if (!inList) { processedLines.push("<ul>"); inList = true; }
      processedLines.push(`<li>${line.replace(/^\s*[-*]\s+/, "")}</li>`);
    } else {
      if (inList) { processedLines.push("</ul>"); inList = false; }
      processedLines.push(line);
    }
  }
  if (inList) processedLines.push("</ul>");
  t = processedLines.join("\n").replace(/\n/g, "<br>");
  return t;
}

// ── Question parser ───────────────────────────────────────────────────────────
// Parses agent response into ordered segments preserving section headers in position.
// Returns segments: Array<{ type: 'text', content } | { type: 'question', id, text, options }>

function parseQuestionsFromText(raw) {
  const lines = raw.split("\n");
  const segments = [];
  let textBuffer = [];
  let currentQ = null;

  const flushText = () => {
    const t = textBuffer.join("\n").trim();
    if (t) segments.push({ type: "text", content: t });
    textBuffer = [];
  };
  const flushQ = () => {
    if (!currentQ) return;
    if (currentQ.options.length >= 2) {
      // Only a real MCQ question if it has at least 2 lettered options
      segments.push({ type: "question", ...currentQ });
    } else {
      // Numbered item without enough options → plain text (e.g. diagnosis findings)
      const lines = [`${currentQ.id}. ${currentQ.text}`];
      currentQ.options.forEach((o) => lines.push(`${o.label}) ${o.text}`));
      const t = lines.join("\n").trim();
      if (t) segments.push({ type: "text", content: t });
    }
    currentQ = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const qMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);
    const optMatch = trimmed.match(/^([A-Z])[).]\s+(.+)/);

    if (qMatch) {
      flushQ();
      flushText();
      currentQ = { id: parseInt(qMatch[1], 10), text: qMatch[2], options: [] };
      continue;
    }
    if (optMatch && currentQ) {
      currentQ.options.push({ label: optMatch[1], text: optMatch[2] });
      continue;
    }
    // Non-question, non-option line
    if (currentQ) {
      // Continuation of question text (no options yet) or end of question block
      if (!currentQ.options.length && trimmed) {
        currentQ.text += " " + trimmed;
      } else {
        flushQ();
        textBuffer.push(line);
      }
    } else {
      textBuffer.push(line);
    }
  }
  flushQ();
  flushText();

  const questions = segments.filter((s) => s.type === "question");
  return { segments, questions };
}

// ── Badges ────────────────────────────────────────────────────────────────────

const PhaseBadge = ({ phase }) => {
  if (!phase) return null;
  const colors = phase === "triage" ? "bg-amber-100 text-amber-700"
    : phase === "diagnosis" ? "bg-blue-100 text-blue-700"
    : phase === "new" ? "bg-gray-100 text-gray-500"
    : "bg-green-100 text-green-700";
  return <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${colors}`}>{phase}</span>;
};

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
    status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"
  }`}>{status}</span>
);

// ── Inline Q&A Form ───────────────────────────────────────────────────────────
// Embedded after an agent email with questions. Each question gets a rich textarea.

// Renders a single question card with answer textarea (used inside segment loop).
const QuestionAnswerCard = ({ question, answer, onAnswer, disabled }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm my-3">
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
      <div className="flex items-start gap-2">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#06b6d4] text-white text-xs font-bold flex items-center justify-center mt-0.5">
          {question.id}
        </span>
        <p className="text-sm font-medium text-[#253439] leading-snug">{question.text}</p>
      </div>
      {question.options.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 ml-8">
          {question.options.map((opt) => (
            <span key={opt.label} className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
              <span className="font-semibold text-gray-600">{opt.label})</span> {opt.text}
            </span>
          ))}
        </div>
      )}
    </div>
    <div className="px-4 py-3">
      <textarea
        value={answer}
        onChange={(e) => onAnswer(e.target.value)}
        disabled={disabled}
        placeholder={question.options.length > 0
          ? "Select an option (A, B, C…) or describe in your own words…"
          : "Type your answer here…"}
        className="w-full text-sm text-[#253439] placeholder-gray-400 border-none outline-none resize-none focus:ring-0 bg-transparent"
        style={{ minHeight: "60px", maxHeight: "160px", fontFamily: FONT }}
        onInput={(e) => {
          e.target.style.height = "60px";
          e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
        }}
      />
    </div>
  </div>
);

// Segment-aware Q&A form — renders text segments and question cards in original order.
const InlineQAForm = ({ segments, questions, onSubmit, disabled }) => {
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(questions.map((q) => [q.id, ""]))
  );
  const [submitted, setSubmitted] = useState(false);

  const setAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));

  const handleSubmit = () => {
    const parts = ["Here are my answers to your diagnostic questions:\n"];
    questions.forEach((q) => {
      parts.push(`${q.id}. ${q.text}\n   Answer: ${(answers[q.id] || "").trim() || "(no answer)"}`);
    });
    onSubmit(parts.join("\n\n"));
    setSubmitted(true);
  };

  const allAnswered = questions.every((q) => (answers[q.id] || "").trim());

  if (submitted) {
    return (
      <div className="mx-5 mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700" style={{ fontFamily: FONT }}>
        Answers submitted — waiting for diagnosis…
      </div>
    );
  }

  return (
    <div className="border-t border-dashed border-cyan-200" style={{ fontFamily: FONT }}>
      <div className="px-5 py-4 bg-cyan-50/30">
        <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide mb-3">
          Answer the questions below — then click Send
        </p>
        {/* Render segments in original order */}
        {segments.map((seg, idx) =>
          seg.type === "text" ? (
            <div key={idx}
              className="text-sm text-[#253439] leading-relaxed mb-1"
              dangerouslySetInnerHTML={{ __html: formatBotMessage(seg.content) }}
            />
          ) : (
            <QuestionAnswerCard
              key={seg.id}
              question={seg}
              answer={answers[seg.id] || ""}
              onAnswer={(v) => setAnswer(seg.id, v)}
              disabled={disabled}
            />
          )
        )}
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={disabled || !allAnswered}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#06b6d4] hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition shadow-sm"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            Send Answers
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Collapsible Email Card (read-only in thread) ──────────────────────────────

const CollapsibleEmailCard = ({ message, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";

  const handleCopy = (e) => {
    e.stopPropagation();
    copyMessageToClipboard(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex mb-2" style={{ fontFamily: FONT }}>
      {/* Left accent bar — cyan for incoming, amber for outgoing */}
      <div className={`w-[3px] rounded-l-xl flex-shrink-0 ${
        isUser
          ? expanded ? "bg-amber-300" : "bg-amber-200"
          : expanded ? "bg-[#06b6d4]" : "bg-cyan-200"
      }`} />
      <div className={`flex-1 overflow-hidden rounded-xl rounded-l-none border transition-all shadow-sm ${
        isUser ? "border-amber-100 bg-[#fffdf7]" : "border-cyan-100 bg-white"
      } ${expanded ? "shadow-md" : ""}`}>

        {/* Header row */}
        <div
          className={`px-5 py-3 flex items-center justify-between gap-4 border-b cursor-pointer transition-colors ${
            isUser ? "bg-[#fdf8ec] border-amber-100 hover:bg-[#fdf4e0]" : "bg-[#f0fdfa] border-cyan-100 hover:bg-cyan-50"
          }`}
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold ${
              isUser ? "bg-[#92400e]" : "bg-[#06b6d4]"
            }`}>
              {isUser ? (message.from?.[0]?.toUpperCase() || "U") : "A"}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-[#253439] truncate">{message.from}</span>
                <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full ${
                  isUser ? "bg-amber-100 text-amber-700" : "bg-cyan-100 text-cyan-700"
                }`}>{isUser ? "sent" : "received"}</span>
              </div>
              {!expanded && (
                <div className="text-xs text-gray-400 truncate max-w-xs">
                  {message.content.replace(/<[^>]*>/g, "").replace(/\n/g, " ").slice(0, 80)}…
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isUser && message.cache_hit && (
              <span className="text-gray-400" title="Response served from cache">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </span>
            )}
            {!isUser && message.latency_ms && (
              <span className="text-[10px] text-gray-400 whitespace-nowrap font-mono">
                {(message.latency_ms / 1000).toFixed(1)}s
                {message.cost_usd ? ` · $${message.cost_usd.toFixed(4)}` : ""}
              </span>
            )}
            <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(message.timestamp)}</span>
            {/* Copy button */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#06b6d4] transition px-1"
              title="Copy as email"
            >
              {copied ? (
                <svg width="13" height="13" fill="none" stroke="#06b6d4" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
              className={`text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded body — always read-only */}
        {expanded && (
          <>
            {message.subject && isUser && (
              <div className="px-5 pt-3">
                <span className="inline-block text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{message.subject}</span>
              </div>
            )}
            {isUser ? (
              <div className="px-5 py-4 text-sm text-[#253439] leading-relaxed">
                {(() => {
                  const lines = (message.content || "").split("\n");
                  const answerLines = lines.filter((l) => /^\s*Your answer:/i.test(l));
                  // If message has answer lines, only display those (full template sent to BE)
                  const displayLines = answerLines.length > 0 ? answerLines : lines;
                  return displayLines.map((line, i) => {
                    const m = line.match(/^(\s*Your answer:\s*)(.*)/i);
                    if (m) return (
                      <div key={i} className="my-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 block mb-0.5">Your answer</span>
                        <span className="text-sm font-semibold text-[#253439]">{m[2] || <span className="text-gray-300 italic font-normal">—</span>}</span>
                      </div>
                    );
                    return <div key={i} className={line.trim() ? "" : "h-2"}>{line}</div>;
                  });
                })()}
              </div>
            ) : (
              <div className="px-5 py-4 text-sm text-[#253439] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatBotMessage(message.content) }} />
            )}
            {!isUser && message.quotedContext && (
              <div className="px-5 pb-4">
                <div className="border-l-[3px] border-gray-200 pl-4 py-0.5">
                  <div className="text-xs text-gray-400 mb-1">On {formatTimestamp(message.quotedTimestamp)}, {message.quotedFrom} wrote:</div>
                  <div className="text-xs text-gray-400 line-clamp-2 italic">{message.quotedContext}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Thinking Indicator ────────────────────────────────────────────────────────

const ThinkingIndicator = () => (
  <div className="flex mb-2" style={{ fontFamily: FONT }}>
    <div className="w-[3px] rounded-l-xl flex-shrink-0 bg-[#06b6d4] opacity-50" />
    <div className="flex-1 overflow-hidden rounded-xl rounded-l-none border border-cyan-100 bg-white shadow-sm">
      <div className="px-5 py-3 flex items-center gap-3 border-b border-cyan-100 bg-[#f0fdfa]">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#06b6d4] text-white text-sm font-semibold">A</div>
        <div>
          <div className="text-sm font-semibold text-[#253439]">Arta TSE Agent</div>
          <div className="text-xs text-gray-400">composing reply…</div>
        </div>
      </div>
      <div className="px-5 py-4 flex items-center gap-2">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-[#06b6d4] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-[#06b6d4] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-[#06b6d4] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
        <span className="text-sm text-gray-400">Analyzing your request...</span>
      </div>
    </div>
  </div>
);

// ── Compose Reply ─────────────────────────────────────────────────────────────
// mode: null | "reply" | "qa"
// "Reply" always visible → plain textarea
// "Reply with Answers" only when last agent message has questions → Q&A form

const ComposeReply = ({ activeCase, onSend, isThinking, messages, inputHint, onFindCases, onSubjectChange }) => {
  const isFirstMessage = !messages || messages.length === 0;
  // Auto-open only for genuinely new cases (turn_count === 0), not old cases loading history
  const isNewCase = !activeCase?.turn_count || activeCase.turn_count === 0;
  const [mode, setMode] = useState(() => isNewCase ? "reply" : null);
  const [editedSubject, setEditedSubject] = useState(activeCase?.subject || "");
  const [copied, setCopied] = useState(false);
  const [composeHeight, setComposeHeight] = useState(null);
  const isResizingRef = useRef(false);
  const editRef = useRef(null);      // contenteditable element
  const modeInitRef = useRef(null);  // tracks which mode has been initialized

  const handleTopDragMouseDown = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    const startY = e.clientY;
    const startH = composeHeight || (window.innerHeight * 0.65);
    const onMove = (ev) => {
      if (!isResizingRef.current) return;
      const delta = startY - ev.clientY; // drag up = increase height
      setComposeHeight(Math.min(Math.max(startH + delta, 200), window.innerHeight * 0.85));
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const lastAgentMsg = [...(messages || [])].reverse().find((m) => m.role === "assistant");
  const strippedContent = lastAgentMsg ? stripEmailPreamble(lastAgentMsg.content) : "";
  const hasQuestions = strippedContent.includes(ANSWER_TOKEN);
  const isClosed = activeCase?.status === "closed";

  // Build the compose editor HTML — [[answer]] tokens become teal editable boxes.
  const buildQATemplateHTML = () => {
    const body = renderEmailBody(strippedContent);
    return `<div style="${IS.wrap}">${body}</div>`;
  };

  // Initialize editor content when mode opens
  useEffect(() => {
    if (!mode || modeInitRef.current === mode) return;
    modeInitRef.current = mode;
    if (!editRef.current) return;
    editRef.current.innerHTML = mode === "qa" ? buildQATemplateHTML() : "";
    editRef.current.focus();
    // Place cursor at end
    const range = document.createRange();
    range.selectNodeContents(editRef.current);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Re-open compose only for new cases (never sent a message), not old ones loading history
  useEffect(() => {
    if (isNewCase && isFirstMessage && !mode) {
      setMode("reply");
      modeInitRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewCase, isFirstMessage]);

  const close = () => { setMode(null); modeInitRef.current = null; };

  const handleSend = () => {
    const raw = (editRef.current?.innerText || "").trim();
    if (!raw || isThinking) return;

    let text = raw;

    if (isFirstMessage && editedSubject.trim() && editedSubject.trim() !== activeCase?.subject) {
      onSubjectChange?.(editedSubject.trim());
    }
    onSend(text);
    close();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); }
    if (e.key === "Escape") close();
  };

  const handleCopy = () => {
    if (!lastAgentMsg) return;
    copyMessageToClipboard(lastAgentMsg);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const openMode = (m) => { setMode(m); };

  // ── Persistent footer bar (always visible) ──────────────────────────────────
  const footerBar = (
    <div className="flex-shrink-0 border-t border-gray-200 bg-white pl-6 pr-20 py-2.5 flex items-center gap-3" style={{ fontFamily: FONT }}>
      {/* Send / Reply button — always shown */}
      <button
        onClick={() => openMode("reply")}
        disabled={isThinking || isClosed}
        title={isClosed ? "Reopen this case to reply" : undefined}
        className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition disabled:opacity-40 disabled:pointer-events-none ${
          mode === "reply"
            ? "bg-[#06b6d4] text-white border-[#06b6d4]"
            : "text-gray-600 border-gray-200 hover:border-[#06b6d4] hover:text-[#06b6d4]"
        }`}
      >
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          {isFirstMessage
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" />
          }
        </svg>
        {isFirstMessage ? "Send Message" : "Reply"}
      </button>
      {/* Reply with Answers — only when agent asked questions */}
      {hasQuestions && (
        <button
          onClick={() => openMode("qa")}
          disabled={isThinking || isClosed}
          title={isClosed ? "Reopen this case to reply" : undefined}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition disabled:opacity-40 disabled:pointer-events-none ${
            mode === "qa"
              ? "bg-[#06b6d4] text-white border-[#06b6d4]"
              : "text-gray-600 border-gray-200 hover:border-[#06b6d4] hover:text-[#06b6d4]"
          }`}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 3H11a2 2 0 000 4h2a2 2 0 000-4zM9 12h6M9 16h4"/>
          </svg>
          Reply with Answers
        </button>
      )}
      {/* Find Similar Cases */}
      {/* {onFindCases && (
        <button
          onClick={onFindCases}
          disabled={isThinking}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-[#06b6d4] hover:text-[#06b6d4] disabled:opacity-40 transition"
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35"/>
          </svg>
          Find Similar Cases
        </button>
      )} */}
      {/* Copy + Export — right side */}
      {lastAgentMsg && (
        <button onClick={handleCopy} className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#06b6d4] transition">
          {copied
            ? <><svg width="13" height="13" fill="none" stroke="#06b6d4" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg><span className="text-[#06b6d4]">Copied!</span></>
            : <><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>Copy as email</>
          }
        </button>
      )}
      {messages.length > 0 && (
        <button
          onClick={() => downloadMarkdown(activeCase, messages)}
          className={`flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#06b6d4] transition ${!lastAgentMsg ? "ml-auto" : ""}`}
        >
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Export .md
        </button>
      )}
    </div>
  );

  if (!mode) return footerBar;

  // ── Expanded composer ────────────────────────────────────────────────────────
  return (
    <div className="flex-shrink-0 flex flex-col bg-white shadow-[0_-6px_24px_rgba(0,0,0,0.08)]"
      style={{ fontFamily: FONT, height: composeHeight ? `${composeHeight}px` : undefined, maxHeight: composeHeight ? undefined : "65vh" }}>

      {/* Top drag handle */}
      <div
        onMouseDown={handleTopDragMouseDown}
        className="flex-shrink-0 h-2 cursor-row-resize flex items-center justify-center border-t-2 border-[#06b6d4] hover:bg-[#f0fdfa] transition-colors group"
        title="Drag to resize"
      >
        <div className="w-10 h-1 rounded-full bg-gray-300 group-hover:bg-[#06b6d4] transition-colors" />
      </div>

      {/* Compose header */}
      <div className="flex items-center justify-between px-6 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[#253439]">
            {mode === "qa" ? "Reply with Answers" : isFirstMessage ? "New Message" : "Reply"}
          </span>
          {lastAgentMsg && (
            <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#06b6d4] transition">
              {copied
                ? <><svg width="12" height="12" fill="none" stroke="#06b6d4" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg><span className="text-[#06b6d4]">Copied!</span></>
                : <><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>Copy as email</>
              }
            </button>
          )}
        </div>
        <button onClick={close} className="text-gray-400 hover:text-gray-600">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* To + Subject lines */}
      <div className="flex items-center gap-3 px-6 py-2 border-b border-gray-100 flex-shrink-0">
        <span className="text-xs font-medium text-gray-400 w-14 flex-shrink-0">To</span>
        <span className="text-sm text-[#253439] font-medium">{activeCase?.to_name || "Arta TSE Agent"}</span>
      </div>
      <div className="flex items-center gap-3 px-6 py-2 border-b border-gray-100 flex-shrink-0">
        <span className="text-xs font-medium text-gray-400 w-14 flex-shrink-0">Subject</span>
        {isFirstMessage ? (
          <input
            type="text"
            value={editedSubject}
            onChange={(e) => setEditedSubject(e.target.value)}
            placeholder="Enter subject…"
            className="flex-1 text-sm text-[#253439] bg-transparent outline-none border-none placeholder-gray-400 min-w-0"
            style={{ fontFamily: FONT }}
          />
        ) : (
          <span className="text-sm text-[#253439] truncate">{activeCase?.subject ? `Re: ${activeCase.subject}` : ""}</span>
        )}
      </div>

      {/* Rich text contenteditable — QA pre-fills formatted template, Reply is blank */}
      <div className="flex-1 overflow-y-auto px-6 py-3">
        <div
          ref={editRef}
          contentEditable={!isThinking}
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          data-placeholder="Write your reply… (⌘Enter to send)"
          className="min-h-full outline-none text-sm text-[#253439] leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none"
          style={{ fontFamily: FONT, minHeight: "120px" }}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-6 pt-3 pb-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
        <span className="text-xs text-gray-400">from {activeCase?.from_name}</span>
        <div className="flex items-center gap-2">
          <button onClick={close} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition">Discard</button>
          <button
            onClick={handleSend}
            disabled={isThinking}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-[#06b6d4] hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            {mode === "qa" ? "Send Answers" : isFirstMessage ? "Send" : "Send Reply"}
          </button>
        </div>
      </div>

    </div>
  );
};

// ── Empty thread state ────────────────────────────────────────────────────────

const EmptyThread = ({ onNew, loading }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-[#FAFAF6]" style={{ fontFamily: FONT }}>
    <div className="w-16 h-16 rounded-full bg-cyan-50 flex items-center justify-center mb-4">
      <svg width="28" height="28" fill="none" stroke="#06b6d4" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    </div>
    <h3 className="text-base font-semibold text-[#253439] mb-1">Select a thread</h3>
    <p className="text-sm text-gray-400 max-w-xs mb-5">Choose a case from the sidebar, or start a new batch diagnostic session.</p>
    <button
      onClick={onNew}
      disabled={loading}
      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#06b6d4] hover:bg-cyan-700 disabled:bg-gray-300 rounded-lg transition"
    >
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      </svg>
      New Batch
    </button>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const BatchMode = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const username = localStorage.getItem("username") || "User";

  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [activeCase, setActiveCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState("");
  const [inputHint, setInputHint] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm }
  const [isDeleting, setIsDeleting] = useState(false);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackBlockedNotice, setFeedbackBlockedNotice] = useState("");
  const feedbackBlockedTimerRef = useRef(null);

  const openFeedbackModal = () => {
    if (!activeCase?.case_id) {
      setFeedbackBlockedNotice("Start a batch case first before sharing feedback.");
      if (feedbackBlockedTimerRef.current) window.clearTimeout(feedbackBlockedTimerRef.current);
      feedbackBlockedTimerRef.current = window.setTimeout(() => setFeedbackBlockedNotice(""), 4000);
      return;
    }
    setShowFeedbackModal(true);
  };

  // Auth-gated citation links (S3-proxied /api/images/...) are rendered as
  // <a class="citation-link" data-doc-url> across several render spots; a single
  // delegated listener opens them with a Bearer token instead of navigating.
  useEffect(() => {
    const handler = (e) => {
      const link = e.target.closest("a.citation-link");
      if (!link) return;
      e.preventDefault();
      const url = link.getAttribute("data-doc-url");
      if (url) openAuthenticatedFile(url);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Case search / status filter. The list itself lives in the sidebar rail now
  // (there is no Inbox column any more), but the state stays here: this
  // component owns `cases`, so it is what can filter them.
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "" = show all

  const filteredCases = cases.filter((c) => {
    const q = searchQuery.trim().toLowerCase();
    const matchSearch = !q ||
      (c.subject || "").toLowerCase().includes(q) ||
      (c.case_id || "").toLowerCase().includes(q);
    const matchStatus = !filterStatus || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const chatBodyRef = useRef(null);

  // On mount: load the case list and, if a case was open before refresh,
  // re-select it and rehydrate its conversation via the same path the sidebar
  // click uses.  The persisted id is read once — a ref prevents a race where
  // a slow fetch re-fires the effect while the first is still pending.
  const restoredRef = useRef(false);
  useEffect(() => {
    (async () => {
      const saved = readPersistedCase();
      let loaded = [];
      setCasesLoading(true);
      try {
        const res = await authFetch("/api/batch/cases");
        if (res.ok) {
          loaded = await res.json();
          setCases(loaded);
        }
      } catch (err) {
        console.error("Failed to load batches:", err);
      } finally {
        setCasesLoading(false);
      }
      if (restoredRef.current) return;
      restoredRef.current = true;
      if (!saved) return;
      const match = loaded.find((c) => c.case_id === saved);
      if (match) handleSelectCase(match);
      else persistActiveCase(null); // stale / deleted — clear
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, isThinking]);

  const fetchCases = async () => {
    setCasesLoading(true);
    try {
      const res = await authFetch("/api/batch/cases");
      if (res.ok) setCases(await res.json());
    } catch (err) {
      console.error("Failed to load batches:", err);
    } finally {
      setCasesLoading(false);
    }
  };

  const handleCreateCase = async ({ subject, from_name, to_name }) => {
    try {
      const res = await authFetch("/api/batch/cases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, from_name, to_name }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed to create batch");
      const newCase = await res.json();
      setCases((prev) => [newCase, ...prev]);
      setActiveCase(newCase);
      persistActiveCase(newCase?.case_id);
      setMessages([]);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  const [isCreating, setIsCreating] = useState(false);

  const handleQuickCreateCase = async () => {
    if (isCreating) return;
    setIsCreating(true);
    const now = new Date();
    const subject = `New case · ${now.toLocaleDateString([], { month: "short", day: "numeric" })} ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    await handleCreateCase({ subject, from_name: username, to_name: "Arta TSE Agent" });
    setIsCreating(false);
  };

  const handleSelectCase = async (c) => {
    setActiveCase(c);
    persistActiveCase(c?.case_id);
    setMessages([]);
    setError("");
    setSessionExpired(false);
    setMessagesLoading(true);
    try {
      const res = await authFetch(`/api/batch/cases/${c.case_id}/messages`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages?.length > 0) {
          setMessages(data.messages.map((m, idx) => ({
            role: m.role,
            from: m.role === "user" ? c.from_name : c.to_name,
            to: m.role === "user" ? c.to_name : c.from_name,
            subject: m.role === "user" ? c.subject : `Re: ${c.subject}`,
            content: m.content,
            timestamp: new Date(c.created_at).getTime() + idx * 60000,
            phase: m.role === "assistant" ? c.phase : null,
            latency_ms: m.latency_ms || null,
            cost_usd: m.cost_usd || null,
          })));
        } else if (c.turn_count > 0 || c.phase !== "new") {
          setSessionExpired(true);
        }
      }
    } catch (err) {
      console.error("Failed to load batch messages:", err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || isThinking || !activeCase) return;
    setError("");

    // Auto-derive subject from first message
    const isFirstTurn = messages.length === 0;
    if (isFirstTurn) {
      const derived = subjectFromFirstMessage(text);
      if (derived && derived !== activeCase.subject) {
        authFetch(`/api/batch/cases/${activeCase.case_id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subject: derived }),
        }).catch(() => {});
        setActiveCase((prev) => ({ ...prev, subject: derived }));
        setCases((prev) => prev.map((c) => c.case_id === activeCase.case_id ? { ...c, subject: derived } : c));
      }
    }

    const userMsg = {
      role: "user",
      from: activeCase.from_name,
      to: activeCase.to_name,
      subject: activeCase.subject,
      content: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsThinking(true);

    try {
      const res = await authFetch("/api/batch/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [{ role: "user", content: text }], case_id: activeCase.case_id }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail || "") || `Server error ${res.status}`);
      }
      const payload = await res.json();
      const responseText = payload.content?.[0]?.text || payload.message || payload.text || "(no response)";
      setMessages((prev) => [...prev, {
        role: "assistant",
        from: activeCase.to_name,
        to: activeCase.from_name,
        subject: `Re: ${activeCase.subject}`,
        content: responseText,
        timestamp: Date.now(),
        phase: payload.phase || null,
        quotedContext: text,
        quotedTimestamp: userMsg.timestamp,
        quotedFrom: activeCase.from_name,
        latency_ms: payload.latency_ms || null,
        // Real turn cost (cache priced) — matches history and the admin page.
        // estimated_usd is input/output only; kept as a fallback for older servers.
        cost_usd: payload.usage?.cost_usd ?? payload.usage?.estimated_usd ?? null,
        cache_hit: payload.cache_hit || false,
      }]);
      if (payload.input_hint) setInputHint(payload.input_hint);
      const updatedFields = {};
      if (payload.phase) updatedFields.phase = payload.phase;
      if (payload.auto_status) updatedFields.status = payload.auto_status;
      if (Object.keys(updatedFields).length) {
        setActiveCase((prev) => ({ ...prev, ...updatedFields }));
        setCases((prev) => prev.map((c) => c.case_id === activeCase.case_id ? { ...c, ...updatedFields } : c));
      }
    } catch (err) {
      setError(err.message || "Failed to send message");
    } finally {
      setIsThinking(false);
    }
  }, [isThinking, activeCase]);

  const handleDeleteCase = useCallback((case_id) => {
    setConfirmDialog({
      title: "Delete this thread?",
      message: "All messages in this thread will be permanently removed.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        setConfirmDialog(null);
        setIsDeleting(true);
        try {
          const res = await authFetch(`/api/batch/cases/${case_id}`, { method: "DELETE" });
          if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Delete failed");
          if (activeCase?.case_id === case_id) { setActiveCase(null); setMessages([]); persistActiveCase(null); }
          await fetchCases();
        } catch (err) {
          setError(err.message || "Failed to delete case");
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }, [activeCase]);

  const handleDeleteAllCases = useCallback(() => {
    if (cases.length === 0) return;
    setConfirmDialog({
      title: `Delete all ${cases.length} thread${cases.length !== 1 ? "s" : ""}?`,
      message: "Every batch discussion for your account will be permanently removed.",
      confirmLabel: "Delete All",
      onConfirm: async () => {
        setConfirmDialog(null);
        setIsDeleting(true);
        try {
          const res = await authFetch("/api/batch/cases", { method: "DELETE" });
          if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Delete all failed");
          setActiveCase(null);
          setMessages([]);
          persistActiveCase(null);
          await fetchCases();
        } catch (err) {
          setError(err.message || "Failed to delete all cases");
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }, [cases.length]);

  const handleReopenCase = useCallback(async () => {
    if (!activeCase) return;
    try {
      const res = await authFetch(`/api/batch/cases/${activeCase.case_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "open" }),
      });
      if (res.ok) {
        setActiveCase((prev) => ({ ...prev, status: "open" }));
        setCases((prev) => prev.map((c) => c.case_id === activeCase.case_id ? { ...c, status: "open" } : c));
      }
    } catch (err) {
      setError(err.message || "Failed to reopen case");
    }
  }, [activeCase]);


  return (
    <div className="flex min-h-screen h-screen w-full bg-white overflow-hidden">
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal
          surface="engineer"
          sessionId={activeCase?.case_id}
          username={username}
          firstPrompt={activeCase?.subject}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
      {/* The rail IS the case list: the middle Inbox column used to show the
          same rows a second time. Everything it did — search, status filter,
          count, per-row delete, delete-all — is handed over as props, so the
          rail stays a view and this component stays the single owner of the
          state. */}
      <SideBar
        mode="agent"
        onLogout={onLogout}
        isDarkMode={false}
        onToggleTheme={() => {}}
        onLinkClick={(p) => navigate(p)}
        currentPath={location.pathname}
        batch={{
          cases,
          filteredCases,
          loading: casesLoading,
          activeCaseId: activeCase?.case_id || "",
          search: searchQuery,
          onSearchChange: setSearchQuery,
          statusFilter: filterStatus,
          onStatusFilterChange: setFilterStatus,
          onSelectCase: handleSelectCase,
          onDeleteCase: handleDeleteCase,
          onDeleteAll: handleDeleteAllCases,
          // A delete is in flight anywhere in the list — both delete controls
          // are disabled while it runs rather than tracked per row, matching
          // what the Inbox column did with the same flag.
          deleting: isDeleting,
        }}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between pl-6 pr-16 py-4 bg-white border-b border-gray-200 flex-shrink-0">
          <div style={{ fontFamily: FONT }}>
            <h1 className="text-base font-semibold text-[#253439]">Batch Diagnostics</h1>
            <p className="text-xs text-gray-400 mt-0.5">AI-assisted email triage for support cases</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openFeedbackModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#06b6d4] hover:bg-cyan-600 rounded-lg transition"
              style={{ fontFamily: FONT }}
              type="button"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Feedback
            </button>
            <button
              onClick={handleQuickCreateCase}
              disabled={casesLoading || isCreating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#06b6d4] hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition"
              style={{ fontFamily: FONT }}
            >
              {isCreating ? (
                <svg className="animate-spin" width="14" height="14" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"/>
                </svg>
              ) : (
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              )}
              {isCreating ? "Creating…" : "New Batch"}
            </button>
          </div>
        </div>
        <div className="absolute top-4 right-4 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        {feedbackBlockedNotice && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800 flex-shrink-0" style={{ fontFamily: FONT }}>
            {feedbackBlockedNotice}
          </div>
        )}

        {/* Thread pane. One child now (the Inbox column moved into the rail),
            kept as a flex row so the thread keeps `flex-1` and fills the width
            the Inbox used to take. */}
        <div className="flex flex-1 overflow-hidden">

          {/* Thread */}
          {activeCase ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Thread header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0" style={{ fontFamily: FONT }}>
                <h2 className="text-base font-semibold text-[#253439] mb-1">{activeCase.subject}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-[#06b6d4] bg-cyan-50 px-2 py-0.5 rounded">{activeCase.case_id}</span>
                  <span className="text-xs text-gray-400">{activeCase.from_name} → {activeCase.to_name}</span>
                  <StatusBadge status={activeCase.status} />
                  {activeCase.status === "closed" && (
                    <button onClick={handleReopenCase} className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition">
                      Reopen
                    </button>
                  )}
                  {/* Total elapsed + cost across all agent turns */}
                  {(() => {
                    const agentMsgs = messages.filter((m) => m.role === "assistant" && m.latency_ms);
                    if (!agentMsgs.length || window.location.hostname !== "zscaler.artasupport.com") return null;
                    const totalMs = agentMsgs.reduce((s, m) => s + (m.latency_ms || 0), 0);
                    const totalCost = agentMsgs.reduce((s, m) => s + (m.cost_usd || 0), 0);
                    return (
                      <span className="ml-auto relative group flex-shrink-0 cursor-help">
                        <span className="text-[10px] font-mono text-gray-400 whitespace-nowrap underline decoration-dotted decoration-gray-300 underline-offset-2">
                          {agentMsgs.length} turn{agentMsgs.length !== 1 ? "s" : ""} · {(totalMs / 1000).toFixed(1)}s total
                          {totalCost > 0 ? ` · $${totalCost.toFixed(4)}` : ""}
                        </span>
                        <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-52 bg-white border border-cyan-100 rounded-xl shadow-xl py-2 text-[10px] font-mono">
                          <div className="px-3 pb-1.5 border-b border-cyan-100 text-[9px] uppercase tracking-wider text-[#06b6d4] font-semibold">Turn breakdown</div>
                          {agentMsgs.map((m, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1 hover:bg-cyan-50">
                              <span className="text-gray-400">#{i + 1}</span>
                              <span className="text-[#253439]">{m.latency_ms > 0 ? `${(m.latency_ms / 1000).toFixed(1)}s` : "—"}</span>
                              <span className="text-[#06b6d4] font-semibold">{m.cost_usd > 0 ? `$${m.cost_usd.toFixed(4)}` : "—"}</span>
                            </div>
                          ))}
                        </div>
                      </span>
                    );
                  })()}
                  {messages.length > 0 && (
                    <button
                      onClick={() => downloadMarkdown(activeCase, messages)}
                      className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#06b6d4] transition px-1.5 py-0.5 rounded hover:bg-cyan-50"
                      title="Export chat as Markdown"
                    >
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Export .md
                    </button>
                  )}
                </div>
              </div>

              {/* Thread messages */}
              <div ref={chatBodyRef} className="flex-1 overflow-y-auto px-6 py-5 bg-[#FAFAF6]">
                {messagesLoading ? (
                  <div className="flex flex-col items-center justify-center h-full text-center" style={{ fontFamily: FONT }}>
                    <div className="w-8 h-8 border-2 border-cyan-200 border-t-cyan-500 rounded-full animate-spin mb-3" />
                    <p className="text-xs text-gray-400">Loading messages...</p>
                  </div>
                ) : messages.length === 0 && !isThinking && sessionExpired ? (
                  <div className="flex flex-col items-center justify-center h-full text-center" style={{ fontFamily: FONT }}>
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="22" height="22" fill="none" stroke="#9ca3af" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-[#253439] mb-1">Session expired</p>
                    <p className="text-xs text-gray-400">The conversation history for this case is no longer available.</p>
                  </div>
                ) : messages.length === 0 && !isThinking ? (
                  <div className="flex flex-col items-center justify-center h-full text-center" style={{ fontFamily: FONT }}>
                    <div className="w-12 h-12 rounded-full bg-cyan-50 flex items-center justify-center mb-3">
                      <svg width="22" height="22" fill="none" stroke="#06b6d4" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-[#253439] mb-1">{activeCase.subject}</p>
                    <p className="text-xs text-gray-400">Hit Reply below to start the diagnostic session.</p>
                  </div>
                ) : (
                  <div className="max-w-3xl mx-auto">
                    {messages.map((msg, idx) => (
                      <CollapsibleEmailCard
                        key={idx}
                        message={msg}
                        defaultExpanded={true}
                      />
                    ))}
                    {isThinking && <ThinkingIndicator />}
                    {error && (
                      <div className="border border-red-200 bg-red-50 rounded-lg px-4 py-3 mb-3 text-sm text-red-700" style={{ fontFamily: FONT }}>
                        {error}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!sessionExpired && (
                <ComposeReply
                  key={activeCase?.case_id}
                  activeCase={activeCase}
                  onSend={sendMessage}
                  isThinking={isThinking}
                  messages={messages}
                  inputHint={inputHint}
                  onFindCases={messages.length > 0 ? () => sendMessage("find similar cases") : null}
                  onSubjectChange={async (newSubject) => {
                    try {
                      await authFetch(`/api/batch/cases/${activeCase.case_id}`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ subject: newSubject }),
                      });
                      setActiveCase((prev) => ({ ...prev, subject: newSubject }));
                      setCases((prev) => prev.map((c) =>
                        c.case_id === activeCase.case_id ? { ...c, subject: newSubject } : c
                      ));
                    } catch { /* silent — subject update is best-effort */ }
                  }}
                />
              )}
            </div>
          ) : (
            <EmptyThread onNew={handleQuickCreateCase} loading={casesLoading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchMode;
